import React from 'react';
import PropTypes from 'prop-types';
import { CheckOutlined, CloseOutlined } from '@ant-design/icons';
import styled from 'styled-components';
import moment from 'moment';
import { Box, Flex, Text } from 'atoms';
import { EyeIconSVG } from 'utils/Svg';
import { ACL } from 'components/common';

const RequestCard = styled(Box)`
	background: #fff;
	border: 1px solid #e8e8e8;
	border-radius: 8px;
	padding: 24px;
	margin-bottom: 16px;
	transition: box-shadow 0.3s;

	&:hover {
		box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
	}
`;

const CardBody = styled(Flex)`
	align-items: flex-start;
	justify-content: space-between;
	gap: 24px;
`;

const InfoColumns = styled(Box)`
	display: grid;
	grid-template-columns: repeat(2, 1fr);
	gap: 20px 40px;
	flex: 1;
`;

const InfoField = styled(Box)`
	display: flex;
	flex-direction: column;
	gap: 8px;
`;

const FieldLabel = styled(Text)`
	font-size: 12px;
	color: #666;
	font-weight: 400;
	line-height: 1.4;
	width: 140px;
`;

const FieldValue = styled(Text)`
	font-size: 15px;
	color: #000;
	font-weight: 600;
	line-height: 1.5;
`;

const ActionSection = styled(Flex)`
	flex-direction: row;
	align-items: center;
	justify-content: center;
	gap: 12px;
	flex-shrink: 0;
	align-self: center;
`;

const ViewButton = styled(Box)`
	display: flex;
	align-items: center;
	justify-content: center;
	width: 32px;
	height: 32px;
	border-radius: 4px;
	border: none;
	cursor: pointer;
	color: #000;
	font-size: 16px;
	transition: all 0.2s ease;
	margin-right: 15px;
	padding: 0;

	span {
		display: flex;
		align-items: center;
		justify-content: center;
		width: 20px !important;
		height: 20px !important;
		margin-right: 0 !important;
	}

	svg {
		width: 20px !important;
		height: 20px !important;
		max-width: 20px;
		max-height: 20px;
	}

	&:hover {
		background: #bae7ff;
	}

	&:active {
		background: #91d5ff;
	}
`;

const ApproveButton = styled.button`
	display: flex;
	align-items: center;
	justify-content: center;
	gap: 8px;

	padding: 8px 18px;
	min-width: 130px;

	background: #fff;
	color: #52c41a;

	border: 1.5px solid #52c41a;
	border-radius: 6px;

	font-size: 14px;
	font-weight: 500;
	cursor: pointer;

	transition: all 0.25s ease;

	.anticon {
		font-size: 16px;
		color: #52c41a;
	}

	&:hover {
		background: #f6ffed;
		border-color: #73d13d;
		color: #73d13d;
	}

	&:active {
		background: #d9f7be;
		border-color: #52c41a;
	}
`;

const RejectButton = styled.button`
	display: flex;
	align-items: center;
	justify-content: center;
	gap: 6px;
	padding: 10px 20px;
	background: #fff;
	color: #f44336;
	border: 1.5px solid #f44336;
	border-radius: 6px;
	font-size: 14px;
	font-weight: 600;
	cursor: pointer;
	transition: all 0.3s;
	min-width: 130px;

	.anticon {
		font-size: 16px;
	}

	&:hover {
		background: #ffebee;
		border-color: #e57373;
		color: #e57373;
		transform: translateY(-1px);
	}

	&:active {
		background: #fff;
		border-color: #d32f2f;
		color: #d32f2f;
		transform: translateY(0);
	}
`;

const formatCurrency = (value) => {
	if (value === null || value === undefined || value === '') return '-';
	return `₹ ${Number(value).toLocaleString('en-IN', {
		minimumFractionDigits: 2,
		maximumFractionDigits: 2,
	})}`;
};

const formatDate = (dateString) => {
	if (!dateString) return '-';
	return moment(dateString).format('DD-MM-YYYY');
};

export default function InvoiceEditApprovalCardV2({
	invoice,
	onView,
	onApprove,
	onReject,
	showActions = true,
}) {
	return (
		<RequestCard>
			<CardBody>
				<InfoColumns>
					<InfoField>
						<FieldLabel>Invoice No :</FieldLabel>
						<FieldValue>
							<strong>{invoice.invoice_no || '-'}</strong>
						</FieldValue>
					</InfoField>

					<InfoField>
						<FieldLabel>Project Name :</FieldLabel>
						<FieldValue>
							<strong>{invoice.project_name || '-'}</strong>
						</FieldValue>
					</InfoField>

					<InfoField>
						<FieldLabel>Vendor Name :</FieldLabel>
						<FieldValue>
							<strong>{invoice.vendor_name || '-'}</strong>
						</FieldValue>
					</InfoField>

					<InfoField>
						<FieldLabel>Initial Invoice Value :</FieldLabel>
						<FieldValue>{formatCurrency(invoice.initial_invoice_value)}</FieldValue>
					</InfoField>

					<InfoField>
						<FieldLabel>Updated Invoice Value :</FieldLabel>
						<FieldValue>{formatCurrency(invoice.updated_invoice_value)}</FieldValue>
					</InfoField>

					<InfoField>
						<FieldLabel>Billing Period :</FieldLabel>
						<FieldValue>{invoice.billing_period || '-'}</FieldValue>
					</InfoField>

					<InfoField>
						<FieldLabel>Created at :</FieldLabel>
						<FieldValue>{formatDate(invoice.created_at)}</FieldValue>
					</InfoField>
				</InfoColumns>
				<ActionSection>
					<ViewButton onClick={() => onView(invoice.id)}>
						<EyeIconSVG />
					</ViewButton>
					{showActions && (
						<>
							<ACL action="update" module="vendor_bill_changes_accept">
								<ApproveButton onClick={() => onApprove(invoice.id)}>
									<CheckOutlined />
									Accept
								</ApproveButton>
							</ACL>
							<ACL action="update" module="vendor_bill_changes_reject">
								<RejectButton onClick={() => onReject(invoice.id)}>
									<CloseOutlined />
									Reject
								</RejectButton>
							</ACL>
						</>
					)}
				</ActionSection>
			</CardBody>
		</RequestCard>
	);
}

InvoiceEditApprovalCardV2.propTypes = {
	invoice: PropTypes.shape({
		id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
		audit_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
		invoice_no: PropTypes.string,
		project_name: PropTypes.string,
		vendor_name: PropTypes.string,
		initial_invoice_value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
		updated_invoice_value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
		billing_period: PropTypes.string,
		created_at: PropTypes.string,
	}).isRequired,
	onView: PropTypes.func.isRequired,
	onApprove: PropTypes.func.isRequired,
	onReject: PropTypes.func.isRequired,
	showActions: PropTypes.bool,
};
